import { useState } from 'react';
import type { ModelRecord } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useDeprecateModel, useUndeprecateModel } from '../api/useModels.ts';
import { useToast, coerceErrorMessage } from './Toast.tsx';
import { useT } from '../i18n/index.ts';
import { Button, Chip, FormField, Panel, Textarea } from '../ui/index.ts';

/**
 * Model deprecation card — deprecate / undeprecate. Epic #109 P2.12.
 *
 * Deprecation is the softer intermediate state between "live" and
 * "soft-deleted". A deprecated model stays listable / chargeable /
 * routable but is flagged in `ModelRecord.deprecated_at` so any UI
 * that cares can surface a migration notice.
 *
 * Lifecycle is separate from PATCH so the audit log has clean
 * `model.deprecate` / `model.undeprecate` rows rather than opaque
 * `model.update` diffs. Notice updates require undeprecate → deprecate
 * (two audit rows) rather than in-place edits.
 */

const DEPRECATION_NOTICE_MAX = 512;

interface Props {
  readonly record: ModelRecord;
}

export function DeprecationCard({ record }: Props): JSX.Element {
  const t = useT();
  const deprecate = useDeprecateModel();
  const undeprecate = useUndeprecateModel();
  const toast = useToast();
  const [notice, setNotice] = useState('');
  const [error, setError] = useState<string | null>(null);

  const busy = deprecate.isPending || undeprecate.isPending;
  const isDeprecated = record.deprecated_at !== null;

  async function handleDeprecate(): Promise<void> {
    setError(null);
    const trimmed = notice.trim();
    if (trimmed.length === 0) {
      setError(t('model_detail.deprecation.error.notice_required'));
      return;
    }
    if (trimmed.length > DEPRECATION_NOTICE_MAX) {
      setError(
        t('model_detail.deprecation.error.notice_too_long', {
          limit: DEPRECATION_NOTICE_MAX,
        }),
      );
      return;
    }
    try {
      await deprecate.mutateAsync({
        vendor: record.vendor,
        model: record.model,
        body: { deprecation_notice: trimmed },
      });
      setNotice('');
      toast.success(t('model_detail.deprecation.toast.deprecated'));
    } catch (err) {
      const message =
        err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  async function handleUndeprecate(): Promise<void> {
    setError(null);
    try {
      await undeprecate.mutateAsync({ vendor: record.vendor, model: record.model });
      toast.success(t('model_detail.deprecation.toast.undeprecated'));
    } catch (err) {
      const message =
        err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <article
      data-testid="model-deprecation-card"
      data-deprecated={isDeprecated ? 'true' : 'false'}
    >
      <Panel caption={t('model_detail.deprecation.title')} padded>
        <div className="flex flex-col gap-4">
          <p className="m-0 text-body text-paper-low">{t('model_detail.deprecation.body')}</p>
        {isDeprecated ? (
          <div
            data-testid="model-deprecation-state"
            className="flex flex-col gap-3 rounded-lg border border-warn-line bg-well p-4"
          >
            <div className="flex flex-wrap items-center gap-2">
              <Chip tone="warn">{t('model_detail.deprecation.chip.deprecated')}</Chip>
              <span
                className="font-mono text-label text-paper-low"
                data-testid="model-deprecation-stamp"
              >
                {record.deprecated_at}
              </span>
            </div>
            {record.deprecation_notice !== null ? (
              <p
                className="m-0 whitespace-pre-wrap text-body text-paper"
                data-testid="model-deprecation-notice"
              >
                {record.deprecation_notice}
              </p>
            ) : null}
            <div>
              <Button
                variant="secondary"
                onClick={() => void handleUndeprecate()}
                disabled={busy}
                loading={undeprecate.isPending}
                data-testid="model-undeprecate"
              >
                {undeprecate.isPending
                  ? t('model_detail.deprecation.undeprecate.pending')
                  : t('model_detail.deprecation.undeprecate.submit')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <FormField
              label={t('model_detail.deprecation.notice.label')}
              fieldId="model-deprecation-notice-input"
              hint={
                <span data-testid="model-deprecation-char-count">
                  {t('model_detail.deprecation.notice.counter', {
                    count: notice.length,
                    limit: DEPRECATION_NOTICE_MAX,
                  })}
                </span>
              }
            >
              <Textarea
                id="model-deprecation-notice-input"
                value={notice}
                onChange={(e) => setNotice(e.currentTarget.value)}
                disabled={busy}
                data-testid="model-deprecation-notice-input"
                rows={3}
                maxLength={DEPRECATION_NOTICE_MAX}
                placeholder={t('model_detail.deprecation.notice.placeholder')}
              />
            </FormField>
            <div>
              <Button
                variant="secondary"
                onClick={() => void handleDeprecate()}
                disabled={busy || notice.trim().length === 0}
                loading={deprecate.isPending}
                data-testid="model-deprecate"
                className="border-warn-line text-warn hover:bg-warn-dim hover:text-warn"
              >
                {deprecate.isPending
                  ? t('model_detail.deprecation.deprecate.pending')
                  : t('model_detail.deprecation.deprecate.submit')}
              </Button>
            </div>
          </div>
        )}
          {error !== null ? (
            <p
              className="m-0 text-label text-bad"
              role="alert"
              data-testid="model-deprecation-error"
            >
              {error}
            </p>
          ) : null}
        </div>
      </Panel>
    </article>
  );
}
